import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import Layout from '../components/Layout';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiConfig';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PÁGINA DE GESTIÓN DE VARIABLES - BIOFIRMAS CTX
 * ═══════════════════════════════════════════════════════════════════════════
 * Permite agregar, editar y eliminar las variables disponibles para
 * las plantillas sin tocar el código.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const CATEGORIAS = [
  { value: 'paciente',      label: '👤 Paciente',           color: 'primary' },
  { value: 'representante', label: '👨‍👩‍👧 Representante',     color: 'warning' },
  { value: 'firmante',      label: '✍️ Firmante',            color: 'success' },
  { value: 'funcionario',   label: '👨‍⚕️ Funcionario',        color: 'info' },
  { value: 'documento',     label: '📄 Documento',           color: 'secondary' },
  { value: 'firmas',        label: '🖼️ Imágenes de Firmas',  color: 'dark' },
  { value: 'otro',          label: '📌 Otro',                color: 'light' },
];

const ORDEN_CATEGORIAS = ['paciente', 'firmante', 'representante', 'funcionario', 'documento', 'firmas', 'otro'];

function VariablesPage() {
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState({ texto: '', tipo: '' });
  const [filtroCategoria, setFiltroCategoria] = useState('todas');

  // Modal crear/editar
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [variableActual, setVariableActual] = useState(null);
  const [formData, setFormData] = useState({ variable: '', descripcion: '', categoria: 'paciente' });
  const [guardando, setGuardando] = useState(false);

  // Modal eliminar
  const [showModalEliminar, setShowModalEliminar] = useState(false);
  const [variableAEliminar, setVariableAEliminar] = useState(null);

  useEffect(() => {
    cargarVariables();
  }, []);

  // =====================================================
  // CARGAR VARIABLES
  // =====================================================
  const cargarVariables = async () => {
    try {
      setLoading(true);
      const data = await apiGet('/api/variables');
      setVariables(data.data || {});
    } catch (error) {
      console.error('❌ Error cargando variables:', error);
      setMensaje({ texto: 'Error al cargar variables', tipo: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const mostrarMensaje = (texto, tipo = 'success') => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje({ texto: '', tipo: '' }), 3000);
  };

  // =====================================================
  // MODAL CREAR / EDITAR
  // =====================================================
  const abrirModalCrear = () => {
    setModoEdicion(false);
    setVariableActual(null);
    setFormData({ variable: '', descripcion: '', categoria: 'paciente' });
    setShowModal(true);
  };

  const abrirModalEditar = (v) => {
    setModoEdicion(true);
    setVariableActual(v);
    setFormData({ variable: v.variable, descripcion: v.descripcion, categoria: v.categoria });
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!formData.variable.trim() || !formData.descripcion.trim()) {
      mostrarMensaje('La variable y la descripción son obligatorias', 'danger');
      return;
    }
    setGuardando(true);
    try {
      if (modoEdicion) {
        await apiPut(`/api/variables/${variableActual._id}`, {
          descripcion: formData.descripcion,
          categoria: formData.categoria
        });
        mostrarMensaje('✅ Variable actualizada correctamente');
      } else {
        await apiPost('/api/variables', formData);
        mostrarMensaje('✅ Variable creada correctamente');
      }
      setShowModal(false);
      cargarVariables();
    } catch (error) {
      mostrarMensaje(error.response?.data?.message || 'Error al guardar variable', 'danger');
    } finally {
      setGuardando(false);
    }
  };

  const toggleActivo = async (v) => {
    try {
      await apiPut(`/api/variables/${v._id}`, { activo: !v.activo });
      mostrarMensaje(`Variable ${v.activo ? 'desactivada' : 'activada'}`);
      cargarVariables();
    } catch (error) {
      mostrarMensaje('Error al cambiar estado', 'danger');
    }
  };

  // =====================================================
  // ELIMINAR
  // =====================================================
  const confirmarEliminar = (v) => {
    setVariableAEliminar(v);
    setShowModalEliminar(true);
  };

  const handleEliminar = async () => {
    if (!variableAEliminar) return;
    try {
      await apiDelete(`/api/variables/${variableAEliminar._id}`);
      mostrarMensaje('✅ Variable eliminada permanentemente');
      setShowModalEliminar(false);
      setVariableAEliminar(null);
      cargarVariables();
    } catch (error) {
      mostrarMensaje('Error al eliminar variable', 'danger');
    }
  };

  const copiarVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    mostrarMensaje(`📋 ${variable} copiada al portapapeles`);
  };

  // =====================================================
  // HELPERS
  // =====================================================
  const getCategoriaInfo = (cat) => CATEGORIAS.find(c => c.value === cat) || CATEGORIAS[CATEGORIAS.length - 1];

  const totalVariables = Object.values(variables).reduce((acc, arr) => acc + arr.length, 0);

  const categoriasFiltradas = filtroCategoria === 'todas'
    ? ORDEN_CATEGORIAS.filter(cat => variables[cat]?.length > 0)
    : [filtroCategoria].filter(cat => variables[cat]?.length > 0);

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <Layout>
      <Container fluid className="mt-4">

        {/* HEADER */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2>🔤 Gestión de Variables</h2>
                <p className="text-muted mb-0">
                  Administra las variables disponibles para las plantillas. Los cambios se aplican inmediatamente.
                </p>
              </div>
              <Button variant="primary" size="lg" onClick={abrirModalCrear}>
                ➕ Nueva Variable
              </Button>
            </div>
          </Col>
        </Row>

        {mensaje.texto && (
          <Alert variant={mensaje.tipo} dismissible onClose={() => setMensaje({ texto: '', tipo: '' })}>
            {mensaje.texto}
          </Alert>
        )}

        {/* RESUMEN + FILTRO */}
        <Row className="mb-4">
          <Col md={8}>
            <Card className="bg-light border-0">
              <Card.Body className="py-3">
                <div className="d-flex gap-3 align-items-center flex-wrap">
                  <div>
                    <strong>Total:</strong> {totalVariables} variables
                  </div>
                  <div className="vr"></div>
                  {ORDEN_CATEGORIAS.filter(cat => variables[cat]?.length > 0).map(cat => {
                    const info = getCategoriaInfo(cat);
                    return (
                      <Badge key={cat} bg={info.color} text={info.color === 'light' ? 'dark' : undefined} style={{ fontSize: 12 }}>
                        {info.label}: {variables[cat]?.length}
                      </Badge>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Form.Select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
              <option value="todas">🔍 Todas las categorías</option>
              {CATEGORIAS.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {/* INFO */}
        <Alert variant="info" className="py-2 mb-4">
          <strong>💡 ¿Cómo funciona?</strong> Agrega variables en formato <code>@NombreVariable@</code> o <code>%VARIABLE%</code>.
          Escríbelas exactamente igual en tus documentos Word y serán reemplazadas automáticamente al generar el PDF.
        </Alert>

        {/* TABLAS POR CATEGORÍA */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Cargando variables...</p>
          </div>
        ) : totalVariables === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <div style={{ fontSize: 48 }}>🔤</div>
              <h4 className="mt-3">No hay variables configuradas</h4>
              <p className="text-muted">Ejecuta el script de migración o crea variables manualmente.</p>
              <Button variant="primary" onClick={abrirModalCrear}>➕ Crear primera variable</Button>
            </Card.Body>
          </Card>
        ) : (
          categoriasFiltradas.map(categoria => {
            const vars = variables[categoria] || [];
            if (vars.length === 0) return null;
            const info = getCategoriaInfo(categoria);
            return (
              <Card key={categoria} className="mb-4 shadow-sm">
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">
                    <Badge bg={info.color} text={info.color === 'light' ? 'dark' : undefined} className="me-2">
                      {vars.length}
                    </Badge>
                    {info.label}
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '280px' }}>Variable</th>
                        <th>Descripción</th>
                        <th style={{ width: '100px' }} className="text-center">Estado</th>
                        <th style={{ width: '160px' }} className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vars.map((v) => (
                        <tr key={v._id} className={!v.activo ? 'table-secondary opacity-75' : ''}>
                          <td>
                            <code style={{ fontSize: 13, fontWeight: 'bold' }}>{v.variable}</code>
                          </td>
                          <td style={{ fontSize: 14 }}>{v.descripcion}</td>
                          <td className="text-center">
                            <Badge bg={v.activo !== false ? 'success' : 'secondary'}>
                              {v.activo !== false ? '✅ Activa' : '⏸ Inactiva'}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              {/* Copiar */}
                              <Button variant="outline-secondary" size="sm" onClick={() => copiarVariable(v.variable)} title="Copiar">📋</Button>
                              {/* Editar */}
                              <Button variant="outline-primary" size="sm" onClick={() => abrirModalEditar(v)} title="Editar">✏️</Button>
                              {/* Activar/Desactivar */}
                              <Button
                                variant={v.activo !== false ? 'outline-secondary' : 'outline-success'}
                                size="sm"
                                onClick={() => toggleActivo(v)}
                                title={v.activo !== false ? 'Desactivar' : 'Activar'}
                              >
                                {v.activo !== false ? '🔴' : '🟢'}
                              </Button>
                              {/* Eliminar */}
                              <Button variant="outline-danger" size="sm" onClick={() => confirmarEliminar(v)} title="Eliminar">🗑️</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            );
          })
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MODAL: CREAR / EDITAR                                              */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{modoEdicion ? '✏️ Editar Variable' : '➕ Nueva Variable'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info" className="py-2" style={{ fontSize: 13 }}>
              💡 Usa el formato <code>@NombreVariable@</code> para variables de texto,
              o <code>%NOMBRE%</code> para marcadores de imagen (firmas).
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Variable *</Form.Label>
              <Form.Control
                type="text"
                value={formData.variable}
                onChange={(e) => setFormData({ ...formData, variable: e.target.value })}
                placeholder="Ej: @DiagnosticoPaciente@"
                disabled={modoEdicion}
              />
              {modoEdicion && <Form.Text className="text-muted">La variable no se puede cambiar para no romper documentos existentes.</Form.Text>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Descripción *</Form.Label>
              <Form.Control
                type="text"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Ej: Diagnóstico del paciente"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Categoría *</Form.Label>
              <Form.Select value={formData.categoria} onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}>
                {CATEGORIAS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button variant="success" onClick={handleGuardar} disabled={guardando}>
              {guardando ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</> : '💾 Guardar'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MODAL: CONFIRMAR ELIMINACIÓN                                        */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Modal show={showModalEliminar} onHide={() => setShowModalEliminar(false)} centered>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>⚠️ Confirmar Eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Estás seguro de que deseas eliminar permanentemente esta variable?</p>
            {variableAEliminar && (
              <Alert variant="warning">
                <strong>Variable:</strong> <code>{variableAEliminar.variable}</code><br />
                <strong>Descripción:</strong> {variableAEliminar.descripcion}
              </Alert>
            )}
            <Alert variant="danger" className="mb-0 py-2">
              <strong>⚠️ Importante:</strong> Si esta variable está en algún Word, quedará sin reemplazar. Verifica antes de eliminar.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModalEliminar(false)}>Cancelar</Button>
            <Button variant="danger" onClick={handleEliminar}>🗑️ Eliminar Permanentemente</Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </Layout>
  );
}

export default VariablesPage;